import { computed, ReadonlySignal, signal, Signal } from '@preact/signals-react';
import { injectable } from 'inversify';

import InformationFilled from '@vp/assets/icons/InformationFilled.svg?react';
import { Success } from '@vp/common/core/OperationResult';
import { ViewModel, ViewModelDispose } from '@vp/common/ui/ViewModel';
import { AccessManagerPort } from '@vp/manager/access/core/interface/AccessManagerPort';
import { ProfileManagerPort } from '@vp/manager/profile/core/interface/ProfileManagerPort';
import { AppNotificationService } from '@vp/notification/AppNotificationService';
import { ProfileAccessModel, Restriction } from '@vp/profile/core/model/ProfileAccessModel';

@injectable()
export class AccessManagerViewModel extends ViewModel implements ViewModelDispose {
  readonly current: ReadonlySignal<ProfileAccessModel> = this.accessPort.access;
  readonly updated: Signal<ProfileAccessModel> = signal(this.current.value);
  readonly applying: Signal<boolean> = signal(false);

  readonly shareButtonDisabled: ReadonlySignal<boolean> = computed(() => this.isShareButtonDisabled());

  readonly changed: ReadonlySignal<boolean> = computed(() => this.isChanged());

  private controller?: AbortController;

  constructor(
    private readonly accessPort: AccessManagerPort,
    private readonly profileManagerPort: ProfileManagerPort,
    private readonly notificationService: AppNotificationService,
  ) {
    super();
  }

  dispose(): void {
    this.controller?.abort();
  }

  shareProfileWithoutPassword = async (): Promise<void> => {
    try {
      const link = this.createLink();
      await navigator.clipboard.writeText(link);
      this.showSuccessShareNotification();
    } catch {
      this.showErrorShareNotification();
    }
  };

  generateRandomCode = async (): Promise<void> => {
    const code = await this.accessPort.generateCode();
    const updated = this.updated.value.changeCode(code);

    if (!this.updated.value.equals(updated)) {
      this.updated.value = updated;
    }
  };

  applyChanges = async (): Promise<void> => {
    this.controller?.abort();
    this.controller = new AbortController();
    this.applying.value = true;

    const result = await this.accessPort.apply(this.updated.value, this.controller);

    if (result instanceof Success) {
      this.showSuccessNotification();
    } else {
      this.showErrorNotification();
    }

    this.applying.value = false;
  };

  cancelChanges = (): void => {
    if (!this.updated.value.equals(this.current.value)) {
      this.updated.value = this.current.value.copy();
    }
  };

  onCodeChange = (value: string): void => {
    const updated = this.updated.value.changeCode(value);
    this.updated.value = updated;
  };

  onFullRestrictionToggle = (): void => {
    const updated = this.updated.value.toggleAll();
    this.updated.value = updated;
  };

  onSectionToggle = (section: Restriction): void => {
    const updated = this.updated.value.toggle(section);
    this.updated.value = updated;
  };

  private showSuccessNotification(): void {
    this.notificationService.enqueue({
      variant: 'success',
      message: 'Настройки доступа успешно применены',
    });
  }

  private showErrorNotification(message?: string): void {
    this.notificationService.enqueue({
      variant: 'error',
      message: 'Что-то пошло не так',
      secondaryMessage: message || 'Не удалось применить настройки доступа.',
    });
  }

  private isChanged(): boolean {
    return !this.current.value.equals(this.updated.value) && this.updated.value.isValid();
  }

  private isShareButtonDisabled(): boolean {
    return !this.current.value.isValid() || this.applying.value;
  }

  private createLink(): string {
    const id = this.profileManagerPort.active.value?.id;
    const code = this.current.value.code;
    return `${window.location.origin}/p/${id}?code=${code}`;
  }

  private showSuccessShareNotification(): void {
    this.notificationService.enqueue({
      variant: 'primary',
      message: 'Ссылка скопирована',
      Icon: InformationFilled,
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
    });
  }

  private showErrorShareNotification(): void {
    this.notificationService.enqueue({
      variant: 'error',
      message: 'Не удалось скопировать ссылку.',
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
    });
  }
}
